import { useState } from "react";
import { useForm } from "react-hook-form";

import { ApiError } from "@/api/client";
import { usePipelines, useSaveDeal } from "@/api/crm";
import { useMe } from "@/api/hooks";
import type { CustomValues, Deal } from "@/api/types";
import { CustomFieldsFieldset } from "@/components/crm/CustomFieldsFieldset";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Alert, TextField } from "@/components/ui";

interface Values { name: string; amount: string; currency: string; expected_close_date: string; pipeline_id: string; stage_id: string }

export function DealForm({ deal, defaultAccount, defaultStageId, onSaved, onCancel }: {
  deal?: Deal; defaultAccount?: PickerValue; defaultStageId?: string;
  onSaved: (d: Deal) => void; onCancel?: () => void;
}) {
  const me = useMe();
  const pipelines = usePipelines();
  const save = useSaveDeal(deal?.id);
  const [account, setAccount] = useState<PickerValue | null>(deal?.account ?? defaultAccount ?? null);
  const [contact, setContact] = useState<PickerValue | null>(deal?.contact ? { id: deal.contact.id, name: deal.contact.full_name } : null);
  const [dealer, setDealer] = useState<PickerValue | null>(deal?.dealer ?? null);
  const [custom, setCustom] = useState<CustomValues>(deal?.custom ?? {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const defaultPipeline = pipelines.data?.find((p) => p.is_default) ?? pipelines.data?.[0];
  const { register, handleSubmit, watch } = useForm<Values>({
    values: {
      name: deal?.name ?? "", amount: deal ? String(deal.amount) : "",
      currency: deal?.currency ?? me.data?.tenant.currency ?? "INR",
      expected_close_date: deal?.expected_close_date ?? "",
      pipeline_id: deal?.pipeline.id ?? defaultPipeline?.id ?? "",
      stage_id: deal?.stage.id ?? defaultStageId ?? "",
    },
    resetOptions: { keepDirtyValues: true },
  });
  const pipelineId = watch("pipeline_id");
  const stages = pipelines.data?.find((p) => p.id === pipelineId)?.stages.filter((s) => s.kind === "open") ?? [];

  const onSubmit = handleSubmit(async (v) => {
    setErrors({}); setFormError(null);
    if (!v.name.trim()) { setErrors({ name: "Deal name is required" }); return; }
    const body: Record<string, unknown> = {
      name: v.name.trim(), amount: v.amount === "" ? 0 : Number(v.amount), currency: v.currency,
      expected_close_date: v.expected_close_date || null, account_id: account?.id ?? null,
      contact_id: contact?.id ?? null, dealer_id: dealer?.id ?? null, custom,
    };
    if (!deal) { body.pipeline_id = v.pipeline_id || null; body.stage_id = v.stage_id || null; }
    try {
      onSaved(await save.mutateAsync(body));
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) setErrors(e.fieldErrors());
      else setFormError(e instanceof Error ? e.message : "Could not save");
    }
  });

  return (
    <form onSubmit={onSubmit} className="space-y-6" noValidate>
      {formError && <Alert>{formError}</Alert>}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2"><TextField label="Deal name *" placeholder="e.g. Brake brackets, 10k pcs/month" {...register("name")} error={errors.name} /></div>
        <RecordPicker label="Account" endpoint="/accounts" value={account} onChange={setAccount} error={errors.account_id} />
        <RecordPicker label="Contact" endpoint="/contacts" value={contact} onChange={setContact} error={errors.contact_id} />
        <div className="grid grid-cols-[1fr_6rem] gap-2">
          <TextField label="Value" type="number" min={0} step="0.01" inputMode="decimal" {...register("amount")} error={errors.amount} />
          <TextField label="Currency" maxLength={3} {...register("currency")} error={errors.currency} />
        </div>
        <TextField label="Expected close" type="date" {...register("expected_close_date")} error={errors.expected_close_date} />
        <RecordPicker label="Through dealer" endpoint="/accounts" params={{ channel: "true" }} placeholder="Dealer or distributor, if any" value={dealer} onChange={setDealer} error={errors.dealer_id} />
        {!deal && (
          <>
            <div>
              <label htmlFor="pipeline_id" className="label">Pipeline</label>
              <select id="pipeline_id" className="input" {...register("pipeline_id")}>
                {pipelines.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="stage_id" className="label">Stage</label>
              <select id="stage_id" className="input" {...register("stage_id")}>
                <option value="">First stage</option>
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </>
        )}
      </div>
      <CustomFieldsFieldset objectType="deal" value={custom} onChange={setCustom} errors={errors} />
      <div className="flex justify-end gap-2">
        {onCancel && <button type="button" className="btn-ghost" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn-primary" disabled={save.isPending}>{save.isPending ? "Saving…" : deal ? "Save changes" : "Create deal"}</button>
      </div>
    </form>
  );
}
